/**
 * Gamepad poll-and-diff module.
 *
 * Usage:
 *   const gp = createGamepadSource();
 *   // In your rAF loop:
 *   gp.poll();
 *   const mv = gp.getMovement(0); // left-stick, Y-flipped
 *
 * Design notes:
 * - `poll()` must be called once per animation frame before any reads.
 * - Raw axis values are returned without deadzone or response-curve filtering;
 *   consumers (Task 4.1) are responsible for that processing.
 * - Y-axis is negated on read: W3C Standard Gamepad axes[1] is positive=down;
 *   we expose positive=up for game-logic convenience.
 * - `wasJustPressed` / `wasJustReleased` return false on the very first poll
 *   because there is no previous snapshot to diff against. This is intentional
 *   and documented here so callers are not surprised.
 */

import type { Vec2 } from './types';
import { GAMEPAD_BUTTON_MAP } from './key-map';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type GamepadButtonName = keyof typeof GAMEPAD_BUTTON_MAP;

export interface GamepadSnapshot {
  readonly connected: boolean;
  readonly index: number;
  /** Raw axes values — NOT Y-flipped. Length 4 (standard mapping). */
  readonly axes: readonly number[];
  readonly buttons: readonly { pressed: boolean; value: number }[];
  readonly timestamp: number;
}

export interface GamepadSource {
  /** Snapshot `navigator.getGamepads()` into internal cache. Call once per rAF. */
  poll(): void;
  /** Left-stick vector. Y is negated (positive = up). Returns {0,0} when disconnected. */
  getMovement(gamepadIndex: number): Vec2;
  /** Right-stick vector. Y is negated (positive = up). Returns {0,0} when disconnected. */
  getAim(gamepadIndex: number): Vec2;
  /** True when button is currently pressed. False when disconnected. */
  isPressed(gamepadIndex: number, button: GamepadButtonName): boolean;
  /**
   * True ONLY on the first poll where button transitions false→true.
   * Returns false on first-ever poll (no previous snapshot).
   */
  wasJustPressed(gamepadIndex: number, button: GamepadButtonName): boolean;
  /**
   * True ONLY on the first poll where button transitions true→false.
   * Returns false on first-ever poll (no previous snapshot).
   */
  wasJustReleased(gamepadIndex: number, button: GamepadButtonName): boolean;
  /**
   * True when the absolute change in the given axis since the last poll
   * exceeds `threshold` (default 0.05). Returns false on first-ever poll.
   */
  axisChangedSinceLastPoll(gamepadIndex: number, axisIndex: number, threshold?: number): boolean;
  /** Raw current snapshot for the given gamepad index. Null if never seen. */
  getSnapshot(gamepadIndex: number): GamepadSnapshot | null;
}

/**
 * Minimal Gamepad shape used by this module. The full W3C `Gamepad` type
 * includes `vibrationActuator` and `hapticActuators` which tests don't need.
 * Accepting this structural subset lets tests inject plain objects without
 * having to satisfy the full browser Gamepad interface.
 */
export interface GamepadLike {
  readonly connected: boolean;
  readonly index: number;
  readonly axes: ArrayLike<number>;
  readonly buttons: ArrayLike<{ readonly pressed: boolean; readonly value: number }>;
  readonly timestamp: number;
  readonly mapping: string;
}

export interface GamepadSourceDeps {
  /** Injectable for tests. Defaults to `() => navigator.getGamepads()`. */
  getGamepads?: () => (GamepadLike | null)[];
}

// ---------------------------------------------------------------------------
// Implementation helpers
// ---------------------------------------------------------------------------

function buildSnapshot(gp: GamepadLike): GamepadSnapshot {
  return {
    connected: gp.connected,
    index: gp.index,
    // Freeze to prevent accidental mutation; copy to break live-reference
    axes: Object.freeze(Array.from(gp.axes)) as readonly number[],
    buttons: Object.freeze(
      Array.from(gp.buttons).map((b) => ({ pressed: b.pressed, value: b.value })),
    ) as readonly { pressed: boolean; value: number }[],
    timestamp: gp.timestamp,
  };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createGamepadSource(deps?: GamepadSourceDeps): GamepadSource {
  const _getGamepads: () => (GamepadLike | null)[] =
    deps?.getGamepads ??
    (() => {
      if (typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function') {
        // navigator.getGamepads() returns Gamepad which satisfies GamepadLike
        return navigator.getGamepads() as unknown as (GamepadLike | null)[];
      }
      return [];
    });

  // current and previous snapshot maps, keyed by gamepad index
  let current = new Map<number, GamepadSnapshot>();
  let previous = new Map<number, GamepadSnapshot>();

  // ---- helpers ----

  function _getCurrentSnapshot(idx: number): GamepadSnapshot | null {
    return current.get(idx) ?? null;
  }

  function _getPreviousSnapshot(idx: number): GamepadSnapshot | null {
    return previous.get(idx) ?? null;
  }

  function _currentButtonPressed(idx: number, btnIdx: number): boolean {
    const snap = _getCurrentSnapshot(idx);
    if (!snap || !snap.connected) return false;
    return snap.buttons[btnIdx]?.pressed ?? false;
  }

  function _previousButtonPressed(idx: number, btnIdx: number): boolean {
    const snap = _getPreviousSnapshot(idx);
    if (!snap) return false; // no previous → treat as not pressed
    return snap.buttons[btnIdx]?.pressed ?? false;
  }

  // ---- public API ----

  return {
    poll(): void {
      // Advance: current becomes previous, then build new current
      previous = current;
      current = new Map<number, GamepadSnapshot>();

      const gamepads = _getGamepads();
      for (let i = 0; i < gamepads.length; i++) {
        const gp = gamepads[i];
        if (gp === null || gp === undefined) {
          // Slot is empty — do NOT store a snapshot (getSnapshot returns null)
          continue;
        }
        current.set(gp.index, buildSnapshot(gp));
      }
    },

    getMovement(idx: number): Vec2 {
      const snap = _getCurrentSnapshot(idx);
      if (!snap || !snap.connected) return { x: 0, y: 0 };
      return {
        x: snap.axes[0] ?? 0,
        y: -(snap.axes[1] ?? 0), // negate: raw positive=down → game positive=up
      };
    },

    getAim(idx: number): Vec2 {
      const snap = _getCurrentSnapshot(idx);
      if (!snap || !snap.connected) return { x: 0, y: 0 };
      return {
        x: snap.axes[2] ?? 0,
        y: -(snap.axes[3] ?? 0), // negate: same convention as left stick
      };
    },

    isPressed(idx: number, button: GamepadButtonName): boolean {
      const btnIdx = GAMEPAD_BUTTON_MAP[button];
      return _currentButtonPressed(idx, btnIdx);
    },

    wasJustPressed(idx: number, button: GamepadButtonName): boolean {
      const btnIdx = GAMEPAD_BUTTON_MAP[button];
      const prev = _getPreviousSnapshot(idx);
      // First-ever poll has no previous → return false
      if (!prev) return false;
      const currentlyPressed = _currentButtonPressed(idx, btnIdx);
      const wasPressed = _previousButtonPressed(idx, btnIdx);
      return currentlyPressed && !wasPressed;
    },

    wasJustReleased(idx: number, button: GamepadButtonName): boolean {
      const btnIdx = GAMEPAD_BUTTON_MAP[button];
      const prev = _getPreviousSnapshot(idx);
      // First-ever poll has no previous → return false
      if (!prev) return false;
      const currentlyPressed = _currentButtonPressed(idx, btnIdx);
      const wasPressed = _previousButtonPressed(idx, btnIdx);
      return !currentlyPressed && wasPressed;
    },

    axisChangedSinceLastPoll(idx: number, axisIndex: number, threshold = 0.05): boolean {
      const prevSnap = _getPreviousSnapshot(idx);
      // First-ever poll: no previous → no diff
      if (!prevSnap) return false;
      const currSnap = _getCurrentSnapshot(idx);
      if (!currSnap) return false;
      const currVal = currSnap.axes[axisIndex] ?? 0;
      const prevVal = prevSnap.axes[axisIndex] ?? 0;
      return Math.abs(currVal - prevVal) > threshold;
    },

    getSnapshot(idx: number): GamepadSnapshot | null {
      return _getCurrentSnapshot(idx);
    },
  };
}
